/**
 * Back-in-stock signup against Klaviyo's fulfillment-centre-specific custom catalog.
 *
 * The catalog id is composed server-side in snippets/bis-catalog-id.liquid and carried on the
 * trigger as data-bis-catalog-id — this file never derives the warehouse itself. The only
 * values it contributes are the email and the newsletter opt-in.
 */

const BIS_ENDPOINT = 'https://a.klaviyo.com/client/back-in-stock-subscriptions/';
const SUBSCRIPTION_ENDPOINT = 'https://a.klaviyo.com/client/subscriptions/';
const REVISION = '2026-07-15';
const CONTENT_TYPE = 'application/vnd.api+json';
const STORAGE_KEY = 'bis:subscribed';

let dialog = null;
let lastTrigger = null;

/* ---------------------------------------------------------------- subscribed-trigger state */

/**
 * Which catalog ids this visitor already signed up for, for the session only. Klaviyo can't be
 * queried from the storefront, so this is a UI affordance — it stops someone re-submitting the
 * same variant and wondering whether the first one worked. Never treated as authoritative.
 *
 * Held in memory as well as sessionStorage: the MutationObserver below runs on every DOM change,
 * and re-parsing storage each time would be needless work on a busy LP.
 */
let subscribed = (() => {
  try {
    return new Set(JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '[]'));
  } catch {
    return new Set();
  }
})();

function rememberSubscribed(catalogId) {
  subscribed.add(catalogId);
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify([...subscribed]));
  } catch {
    // Private mode / storage disabled — the in-memory marking still applies for this page.
  }
}

/**
 * aria-disabled rather than the `disabled` property, deliberately: a disabled button isn't
 * focusable, so <dialog>'s focus restore on close would find a dead element and drop focus to
 * <body>, losing a keyboard or screen-reader user's place entirely.
 */
function markTrigger(trigger, label) {
  if (trigger.dataset.bisSubscribed) return;
  trigger.dataset.bisSubscribed = 'true';
  trigger.setAttribute('aria-disabled', 'true');
  if (label) trigger.textContent = label;
}

function templateEl() {
  return document.querySelector('.bis-notify-template');
}

/** Re-apply to any triggers now in the DOM — the PDP swaps <buy-buttons> on variant change. */
function markKnownTriggers() {
  if (!subscribed.size) return;
  const label = templateEl()?.content.firstElementChild?.dataset.msgSubscribedTrigger;

  document.querySelectorAll('[data-bis-catalog-id]:not([data-bis-subscribed])').forEach((t) => {
    if (subscribed.has(t.dataset.bisCatalogId)) markTrigger(t, label);
  });
}

/* ------------------------------------------------------------------------------- the dialog */

/** Clone the first template into <body>, once per page. */
function ensureDialog() {
  if (dialog) return dialog;

  const template = templateEl();
  if (!template) return null;

  dialog = template.content.firstElementChild.cloneNode(true);
  document.body.appendChild(dialog);

  dialog.querySelector('.bis-dialog__close')?.addEventListener('click', () => dialog.close());
  dialog.querySelector('.bis-dialog__form')?.addEventListener('submit', onSubmit);

  // Click on the backdrop (the dialog element itself, outside the card) closes.
  dialog.addEventListener('click', (event) => {
    if (event.target === dialog) dialog.close();
  });

  // <dialog> restores focus to the opener, but only if it's still in the DOM. The PDP replaces
  // <buy-buttons> on variant change, so re-point focus defensively on close.
  dialog.addEventListener('close', () => {
    reset();
    if (lastTrigger?.isConnected) lastTrigger.focus();
  });

  return dialog;
}

function setStatus(message, state) {
  const status = dialog.querySelector('.bis-dialog__status');
  status.textContent = message || '';
  status.dataset.state = state || '';
  // On a terminal state the form has nothing left to do — hide it so the message is the content.
  dialog.querySelector('.bis-dialog__content').hidden = state === 'success' || state === 'already';
}

function setLoading(isLoading) {
  const submit = dialog.querySelector('.bis-dialog__submit');
  const label = submit.querySelector('.bis-dialog__submit-label');
  submit.disabled = isLoading;
  submit.setAttribute('aria-busy', String(isLoading));

  if (isLoading) {
    label.dataset.idleLabel ??= label.textContent;
    label.textContent = dialog.dataset.msgSubmitting;
  } else if (label.dataset.idleLabel) {
    label.textContent = label.dataset.idleLabel;
  }
}

function reset() {
  setStatus('', '');
  dialog.querySelector('.bis-dialog__form').reset();
  setLoading(false);
}

/**
 * Klaviyo appears to be idempotent here — a repeat profile+variant returns 202, so this branch
 * is a net rather than a routine path. Kept because the API contract isn't documented either way.
 */
function isDuplicate(status, errors) {
  if (status === 409) return true;
  return errors.some((error) =>
    /already|duplicate|exists/i.test(`${error.code || ''} ${error.title || ''} ${error.detail || ''}`)
  );
}

function post(url, companyId, payload) {
  return fetch(`${url}?company_id=${encodeURIComponent(companyId)}`, {
    method: 'POST',
    headers: { Accept: 'application/json', 'Content-Type': CONTENT_TYPE, revision: REVISION },
    body: JSON.stringify(payload),
  });
}

async function subscribe(email, catalogId, companyId) {
  const response = await post(BIS_ENDPOINT, companyId, {
    data: {
      type: 'back-in-stock-subscription',
      attributes: {
        channels: ['EMAIL'],
        profile: { data: { type: 'profile', attributes: { email } } },
      },
      relationships: {
        variant: { data: { type: 'catalog-variant', id: catalogId } },
      },
    },
  });

  // Success is specifically 202 Accepted with an empty body. Checking response.ok instead would
  // let any other 2xx through, which is exactly how a catalog-id mismatch would hide itself.
  if (response.status === 202) return 'success';

  let errors = [];
  try {
    ({ errors = [] } = await response.json());
  } catch {
    // Non-JSON error body; fall through to the generic message.
  }

  if (isDuplicate(response.status, errors)) return 'already';

  console.warn('[bis] subscription failed', response.status, catalogId, errors);
  return 'error';
}

/**
 * Newsletter opt-in is a SEPARATE consent record, never a side effect of a stock alert — and
 * never a prerequisite for it. Fired after the BIS subscription and deliberately not allowed to
 * affect the outcome the shopper sees.
 */
async function subscribeToNewsletter(email, listId, companyId, { lang, language }) {
  // Same profile properties the footer signup forms set, so segments keyed on them match.
  const properties = {};
  if (lang) properties.$lang = lang;
  if (language) properties.Language = language;

  try {
    const response = await post(SUBSCRIPTION_ENDPOINT, companyId, {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: 'Back in stock form',
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email,
                subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } },
                properties,
              },
            },
          },
        },
        relationships: { list: { data: { type: 'list', id: listId } } },
      },
    });

    if (response.status !== 202) {
      console.warn('[bis] newsletter opt-in failed', response.status, await response.text());
    }
  } catch (error) {
    console.warn('[bis] newsletter opt-in failed', error);
  }
}

async function onSubmit(event) {
  event.preventDefault();

  const input = dialog.querySelector('.bis-dialog__input');
  const newsletter = dialog.querySelector('.bis-dialog__newsletter');
  const { catalogId, company, listId, lang, language } = dialog.dataset;
  const email = input.value.trim();

  if (!input.checkValidity() || !email) {
    setStatus(dialog.dataset.msgInvalid, 'error');
    input.focus();
    return;
  }

  // Never guess a warehouse. Liquid suppresses the button when it can't resolve one, so this is
  // a backstop against a trigger reaching the DOM without a usable id.
  if (!catalogId || !company) {
    console.warn('[bis] missing subscription data', { catalogId, company });
    setStatus(dialog.dataset.msgError, 'error');
    return;
  }

  // No pending message in the status region — the submit button already carries the loading
  // label and aria-busy, and showing both duplicated the same sentence on screen.
  setLoading(true);

  let result;
  try {
    result = await subscribe(email, catalogId, company);
    if (result === 'success' && newsletter?.checked && listId) {
      await subscribeToNewsletter(email, listId, company, { lang, language });
    }
  } catch (error) {
    console.warn('[bis] request failed', error);
    result = 'error';
  } finally {
    setLoading(false);
  }

  if (result === 'success' || result === 'already') {
    rememberSubscribed(catalogId);
    if (lastTrigger) markTrigger(lastTrigger, dialog.dataset.msgSubscribedTrigger);
  }

  setStatus(
    {
      success: dialog.dataset.msgSuccess,
      already: dialog.dataset.msgAlready,
      error: dialog.dataset.msgError,
    }[result],
    result
  );
}

function open(trigger) {
  if (!ensureDialog()) return;

  lastTrigger = trigger;
  dialog.dataset.catalogId = trigger.dataset.bisCatalogId || '';

  const title = dialog.querySelector('[data-bis-product-title]');
  const productTitle = trigger.dataset.bisTitle;
  title.textContent = productTitle || '';
  title.hidden = !productTitle;

  reset();
  dialog.showModal();
  dialog.querySelector('.bis-dialog__input').focus();
}

// Delegated so triggers survive the <buy-buttons> swap on variant change.
document.addEventListener('click', (event) => {
  // Synthetic clicks (GTM / Elevar dispatch them) can target document, which has no closest().
  if (!(event.target instanceof Element)) return;
  const trigger = event.target.closest('[data-bis-catalog-id]');
  if (!trigger) return;
  event.preventDefault();
  // aria-disabled is advisory, so the click still arrives — refuse it here.
  if (trigger.getAttribute('aria-disabled') === 'true') return;
  open(trigger);
});

markKnownTriggers();

// Variant changes replace <buy-buttons> wholesale, bringing in fresh unmarked triggers.
// Coalesced to one pass per frame; markKnownTriggers() also early-returns when nothing is
// subscribed, which is the common case.
let markQueued = false;
new MutationObserver(() => {
  if (markQueued) return;
  markQueued = true;
  requestAnimationFrame(() => {
    markQueued = false;
    markKnownTriggers();
  });
}).observe(document.body, { childList: true, subtree: true });
